import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, IconButton,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  WaterDrop as WaterIcon,
  Agriculture as MillIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const statusColors: Record<string, 'default' | 'warning' | 'info' | 'primary' | 'success' | 'error'> = {
  pending: 'default',
  first_temper: 'warning',
  second_temper: 'info',
  ready: 'primary',
  milled: 'success',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  first_temper: '1st Temper',
  second_temper: '2nd Temper',
  ready: 'Ready',
  milled: 'Milled',
};

interface ActionDialog {
  type: 'start-first' | 'end-first' | 'end-second' | 'mark-milled';
  recordId: string;
  recordNumber: string;
}

const TemperingList: React.FC = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dialog, setDialog] = useState<ActionDialog | null>(null);
  const [waterAdded, setWaterAdded] = useState('');
  const [moisture, setMoisture] = useState('');
  const [finalMoisture, setFinalMoisture] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRecords();
  }, [statusFilter]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      const res = await millingApi.getTemperingRecords(params);
      setRecords(res.data || res || []);
    } catch {
      setError('Failed to load tempering records.');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type: ActionDialog['type'], record: any) => {
    setDialog({ type, recordId: record.id, recordNumber: record.recordNumber });
    setWaterAdded('');
    setMoisture('');
    setFinalMoisture('');
  };

  const handleAction = async () => {
    if (!dialog) return;
    setSaving(true);
    try {
      if (dialog.type === 'start-first') {
        await millingApi.startFirstTemper(dialog.recordId, waterAdded ? Number(waterAdded) : undefined);
      } else if (dialog.type === 'end-first') {
        await millingApi.endFirstTemper(dialog.recordId, moisture ? Number(moisture) : undefined);
      } else if (dialog.type === 'end-second') {
        await millingApi.endSecondTemper(
          dialog.recordId,
          moisture ? Number(moisture) : undefined,
          finalMoisture ? Number(finalMoisture) : undefined,
        );
      } else if (dialog.type === 'mark-milled') {
        await millingApi.markMilled(dialog.recordId);
      }
      setDialog(null);
      fetchRecords();
    } catch {
      setError('Action failed. Check record status.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d?: string | Date) => d ? new Date(d).toLocaleString() : '-';

  const renderActions = (record: any) => {
    const s = record.status;
    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {s === 'pending' && (
          <Tooltip title="Start 1st Temper">
            <IconButton size="small" color="warning" onClick={() => openDialog('start-first', record)}>
              <WaterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {s === 'first_temper' && (
          <Tooltip title="End 1st Temper">
            <IconButton size="small" color="info" onClick={() => openDialog('end-first', record)}>
              <CompleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {s === 'second_temper' && (
          <Tooltip title="End 2nd Temper">
            <IconButton size="small" color="primary" onClick={() => openDialog('end-second', record)}>
              <CompleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {s === 'ready' && (
          <Tooltip title="Mark as Milled">
            <IconButton size="small" color="success" onClick={() => openDialog('mark-milled', record)}>
              <MillIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Tempering Records</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/milling/tempering/new')}>
          New Record
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="first_temper">1st Temper</MenuItem>
              <MenuItem value="second_temper">2nd Temper</MenuItem>
              <MenuItem value="ready">Ready</MenuItem>
              <MenuItem value="milled">Milled</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Card}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Record #</TableCell>
                <TableCell>Grain Lot</TableCell>
                <TableCell>Batch (MT)</TableCell>
                <TableCell>Initial %</TableCell>
                <TableCell>Target %</TableCell>
                <TableCell>Water (L)</TableCell>
                <TableCell>1st Temper</TableCell>
                <TableCell>2nd Temper</TableCell>
                <TableCell>Final %</TableCell>
                <TableCell>Operator</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No tempering records found.
                  </TableCell>
                </TableRow>
              ) : (
                records.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>{r.recordNumber}</TableCell>
                    <TableCell>{r.grainLotNumber}</TableCell>
                    <TableCell>{Number(r.batchQtyMt).toFixed(3)}</TableCell>
                    <TableCell>{r.initialMoisture != null ? `${r.initialMoisture}%` : '-'}</TableCell>
                    <TableCell>{r.targetMoisture != null ? `${r.targetMoisture}%` : '-'}</TableCell>
                    <TableCell>{r.waterAddedLiters != null ? Number(r.waterAddedLiters).toFixed(1) : '-'}</TableCell>
                    <TableCell>
                      {r.firstTemperStart ? (
                        <Box>
                          <Typography variant="caption" display="block">Start: {formatDate(r.firstTemperStart)}</Typography>
                          {r.firstTemperEnd && <Typography variant="caption" display="block">End: {formatDate(r.firstTemperEnd)}</Typography>}
                          {r.firstTemperMoisture != null && <Typography variant="caption" color="text.secondary">{r.firstTemperMoisture}%</Typography>}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {r.secondTemperStart ? (
                        <Box>
                          <Typography variant="caption" display="block">Start: {formatDate(r.secondTemperStart)}</Typography>
                          {r.secondTemperEnd && <Typography variant="caption" display="block">End: {formatDate(r.secondTemperEnd)}</Typography>}
                          {r.secondTemperMoisture != null && <Typography variant="caption" color="text.secondary">{r.secondTemperMoisture}%</Typography>}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {r.finalMoisture != null ? (
                        <Chip
                          label={`${r.finalMoisture}%`}
                          size="small"
                          color={Number(r.finalMoisture) <= Number(r.targetMoisture || 15) ? 'success' : 'error'}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>{r.operatorName || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[r.status] || r.status}
                        size="small"
                        color={statusColors[r.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>{renderActions(r)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Action Dialogs */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        {dialog && (
          <>
            <DialogTitle>
              {dialog.type === 'start-first' && `Start 1st Temper — ${dialog.recordNumber}`}
              {dialog.type === 'end-first' && `End 1st Temper — ${dialog.recordNumber}`}
              {dialog.type === 'end-second' && `End 2nd Temper — ${dialog.recordNumber}`}
              {dialog.type === 'mark-milled' && `Mark as Milled — ${dialog.recordNumber}`}
            </DialogTitle>
            <DialogContent>
              {dialog.type === 'start-first' && (
                <TextField
                  label="Water Added (Liters)"
                  type="number"
                  fullWidth
                  value={waterAdded}
                  onChange={(e) => setWaterAdded(e.target.value)}
                  sx={{ mt: 1 }}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              )}
              {dialog.type === 'end-first' && (
                <TextField
                  label="Moisture After 1st Temper (%)"
                  type="number"
                  fullWidth
                  value={moisture}
                  onChange={(e) => setMoisture(e.target.value)}
                  sx={{ mt: 1 }}
                  inputProps={{ step: 0.01, min: 0, max: 30 }}
                />
              )}
              {dialog.type === 'end-second' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField
                    label="Moisture After 2nd Temper (%)"
                    type="number"
                    fullWidth
                    value={moisture}
                    onChange={(e) => setMoisture(e.target.value)}
                    inputProps={{ step: 0.01, min: 0, max: 30 }}
                  />
                  <TextField
                    label="Final Moisture (%)"
                    type="number"
                    fullWidth
                    value={finalMoisture}
                    onChange={(e) => setFinalMoisture(e.target.value)}
                    inputProps={{ step: 0.01, min: 0, max: 30 }}
                  />
                </Box>
              )}
              {dialog.type === 'mark-milled' && (
                <Typography sx={{ mt: 1 }}>
                  Confirm this batch has been sent to the mill. Status will change to Milled.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)}>Cancel</Button>
              <Button variant="contained" onClick={handleAction} disabled={saving}>
                {saving ? 'Saving...' : 'Confirm'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TemperingList;
